import { z } from "zod";

import { DatabaseDefaults } from "@/src/models/database-defaults";

export const UNLISTED_PROVIDER_VALUE = "unlisted_provider" as const;

export enum ClassificationConfidence {
  HIGH = "high",
  LOW = "low",
  MEDIUM = "medium",
}

export enum IneligibilityReason {
  INELIGIBLE_INSURANCE = "ineligible_insurance",
  MISSING_EMAIL = "missing_email",
  MISSING_MEDICAL_INFO = "missing_medical_info",
  MISSING_REFERRAL_INFO = "missing_referral_info",
  PROGRAM_RESTRICTION = "program_restriction",
  UNKNOWN = "unknown",
  UNSUPPORTED_DIAGNOSIS = "unsupported_diagnosis",
  UNSUPPORTED_PROVIDER = "unsupported_provider",
}

export enum OutOfScopeIntent {
  AUTHORIZATION_OR_REFERRAL_QUESTION = "authorization_or_referral_question",
  BILLING_OR_PRICING = "billing_or_pricing",
  CANCELLING_APPOINTMENT = "cancelling_appointment",
  CLINICAL_QUESTION = "clinical_question",
  FORMS_OR_MEDICAL_RECORDS = "forms_or_medical_records",
  INSURANCE_QUESTION = "insurance_question",
  LANGUAGE_SUPPORT = "language_support",
  MEDICATION_REFILL = "medication_refill",
  MESSAGE_FOR_STAFF = "message_for_staff",
  MID_CALL_INTENT_CHANGE = "mid_call_intent_change",
  NEW_APPOINTMENT_SCHEDULING = "new_appointment_scheduling",
  NON_SCHEDULING_OTHER = "non_scheduling_other",
  TELEHEALTH_OR_CONNECTION_HELP = "telehealth_or_connection_help",
  UNKNOWN = "unknown",
}

export enum PatientLookupIssue {
  LOOKUP_FAILURE = "lookup_failure",
  NEW_PATIENT = "new_patient",
  UNKNOWN = "unknown",
}

export enum PatientRequestDriver {
  NO_AVAILABILITY_FOUND = "no_availability_found",
  NO_BOOKED_APPOINTMENTS_FOUND = "no_booked_appointments_found",
  OTHER = "other",
  PREFERRED_TIME_NOT_AVAILABLE = "preferred_time_not_available",
  PREFERS_HUMAN = "prefers_human",
  SPECIFIC_PERSON_OR_DEPARTMENT = "specific_person_or_department",
  UNCLEAR = "unclear",
}

export enum PatientRequestTiming {
  DELAYED = "delayed",
  IMMEDIATE = "immediate",
  UNKNOWN = "unknown",
}

export enum ToolErrorStage {
  APPOINTMENT_LOOKUP = "appointment_lookup",
  AVAILABILITY_LOOKUP = "availability_lookup",
  CONFIRM = "confirm",
  ELIGIBILITY_CHECK = "eligibility_check",
  FINALIZE_RESCHEDULE = "finalize_reschedule",
  PATIENT_LOOKUP = "patient_lookup",
  UNKNOWN = "unknown",
}

export enum TransferPrimaryReason {
  APPOINTMENT_NOT_FOUND = "appointment_not_found",
  INELIGIBLE = "ineligible",
  OUT_OF_SCOPE_INTENT = "out_of_scope_intent",
  PATIENT_NOT_FOUND = "patient_not_found",
  PATIENT_REQUEST = "patient_request",
  TOOL_ERROR = "tool_error",
  UNKNOWN = "unknown",
  URGENT_CALL = "urgent_call",
}

// Per-value descriptions for the transfer-classification enums. Each map is keyed by the
// full enum, so adding a value forces adding its description (compile error otherwise), and
// each schema's `.describe()` text is generated from its map — new values document
// themselves rather than needing a hand-edited prose list.
function toEnumBullets<K extends string>(descriptions: Record<K, string>): string {
  return Object.entries<string>(descriptions)
    .map(([value, description]) => `- ${value} — ${description}`)
    .join("\n");
}

const TRANSFER_PRIMARY_REASON_DESCRIPTIONS: Record<TransferPrimaryReason, string> = {
  [TransferPrimaryReason.INELIGIBLE]: `the caller cannot proceed through self-service for an eligibility-type reason (provider, diagnosis, referral, medical info, insurance, email, or program restriction). See the ineligibility field for the specific reason. A "no insurance on file", "insurance changed", or "insurance mismatch" situation is ineligible (ineligibility.reason = ineligible_insurance), NOT patient_not_found and NOT tool_error.`,
  [TransferPrimaryReason.OUT_OF_SCOPE_INTENT]:
    "the caller's intent is not something this workflow handles. See outOfScopeIntent.",
  [TransferPrimaryReason.PATIENT_NOT_FOUND]:
    "the patient identity lookup itself failed (could not find or verify the patient). Do NOT use when the patient was found but lacks insurance or other information.",
  [TransferPrimaryReason.PATIENT_REQUEST]: "the caller asked to be transferred to a human.",
  [TransferPrimaryReason.APPOINTMENT_NOT_FOUND]:
    "the patient was found, but the specific appointment they wanted to reschedule, cancel, or confirm could not be located.",
  [TransferPrimaryReason.TOOL_ERROR]: `a tool itself crashed or returned an unexpected error (an operational, system, or workflow failure), not a valid result such as a "no insurance" response. See toolErrorStage.`,
  [TransferPrimaryReason.URGENT_CALL]:
    "use ONLY when the call involves a medical emergency, a patient safety concern, or the agent explicitly delivered triage/safety instructions (e.g., advised calling 911). Do NOT use this when a patient simply requests an earlier or same-day appointment slot.",
  [TransferPrimaryReason.UNKNOWN]: "the transfer reason cannot be determined from the transcript.",
};

const transferPrimaryReasonSchema = z
  .nativeEnum(TransferPrimaryReason)
  .describe(
    `The single best top-level explanation for why the call was transferred to a human team member.\n\n${toEnumBullets(TRANSFER_PRIMARY_REASON_DESCRIPTIONS)}`
  );

const patientRequestTimingSchema = z
  .nativeEnum(PatientRequestTiming)
  .nullable()
  .describe(
    "When primaryReason is patient_request, capture whether the caller asked for a human immediately, later in the conversation, or it is unclear. Otherwise return null."
  );

const PATIENT_REQUEST_DRIVER_DESCRIPTIONS: Record<PatientRequestDriver, string> = {
  [PatientRequestDriver.SPECIFIC_PERSON_OR_DEPARTMENT]: `the caller names a role, department, or specific individual (e.g., "front desk", "receptionist", "Dr. Smith", "billing department").`,
  [PatientRequestDriver.PREFERS_HUMAN]:
    "the caller expresses a general preference for a human without specifying who or which department.",
  [PatientRequestDriver.NO_AVAILABILITY_FOUND]: "no appointment availability was found for the caller.",
  [PatientRequestDriver.NO_BOOKED_APPOINTMENTS_FOUND]: "no existing or booked appointment was found for the caller.",
  [PatientRequestDriver.PREFERRED_TIME_NOT_AVAILABLE]: "the caller's preferred time was not available.",
  [PatientRequestDriver.OTHER]: "the caller wanted a human for a reason that does not fit any bucket above.",
  [PatientRequestDriver.UNCLEAR]: "the reason the caller wanted a human cannot be determined from the transcript.",
};

const patientRequestDriverSchema = z
  .nativeEnum(PatientRequestDriver)
  .nullable()
  .describe(
    `When primaryReason is patient_request, infer the main reason the caller wanted a human. Otherwise return null.\n\n${toEnumBullets(PATIENT_REQUEST_DRIVER_DESCRIPTIONS)}`
  );

const OUT_OF_SCOPE_INTENT_DESCRIPTIONS: Record<OutOfScopeIntent, string> = {
  [OutOfScopeIntent.AUTHORIZATION_OR_REFERRAL_QUESTION]:
    "the caller is asking about the status of a referral or prior authorization.",
  [OutOfScopeIntent.BILLING_OR_PRICING]: "charges, costs, payment, or pricing questions.",
  [OutOfScopeIntent.INSURANCE_QUESTION]:
    "the caller is asking whether their insurance is accepted, in-network, or compatible, OR is calling to update/provide insurance information. Distinct from billing_or_pricing, which covers charges, costs, and payment questions.",
  [OutOfScopeIntent.CANCELLING_APPOINTMENT]:
    "the caller wants to cancel an existing appointment and this workflow does not support cancellation.",
  [OutOfScopeIntent.CLINICAL_QUESTION]: `requests for clinical advice, symptom assessment, or "should I come in?" type questions. Do NOT use this when the caller wants documents or results — use forms_or_medical_records instead.`,
  [OutOfScopeIntent.FORMS_OR_MEDICAL_RECORDS]:
    "the caller wants to obtain, send, or receive documents, records, test results, or lab results.",
  [OutOfScopeIntent.LANGUAGE_SUPPORT]: "the caller needs an interpreter or a language the agent does not speak.",
  [OutOfScopeIntent.MEDICATION_REFILL]: "a request to refill or renew a prescription.",
  [OutOfScopeIntent.MESSAGE_FOR_STAFF]:
    "the caller wants to leave a message, request a callback, or leave a voicemail for a person or department. Prefer this over primaryReason: patient_request / prefers_human for callback or voicemail requests — the caller's goal is to leave a message, not to be transferred to a live agent for its own sake.",
  [OutOfScopeIntent.NEW_APPOINTMENT_SCHEDULING]:
    "use ONLY when this agent's workflow does not support new appointment scheduling for this queue at all (i.e., the agent is simply not capable of booking appointments in this context). Do NOT use this when a scheduling attempt starts but fails mid-flow due to insurance, clinical eligibility, or a tool error — those belong in primaryReason: ineligible or primaryReason: tool_error.",
  [OutOfScopeIntent.TELEHEALTH_OR_CONNECTION_HELP]: "help joining a telehealth visit or technical support.",
  [OutOfScopeIntent.NON_SCHEDULING_OTHER]: "an out-of-scope intent that does not fit any bucket above.",
  [OutOfScopeIntent.MID_CALL_INTENT_CHANGE]:
    "there is evidence the caller changed their intent after an initial intent was selected.",
  [OutOfScopeIntent.UNKNOWN]: "out of scope, but the specific bucket cannot be determined from the transcript.",
};

const outOfScopeIntentSchema = z
  .nativeEnum(OutOfScopeIntent)
  .nullable()
  .describe(
    `When primaryReason is out_of_scope_intent, choose the most specific unsupported intent bucket. Otherwise return null.\n\n${toEnumBullets(OUT_OF_SCOPE_INTENT_DESCRIPTIONS)}`
  );

const toolErrorStageSchema = z
  .nativeEnum(ToolErrorStage)
  .nullable()
  .describe("When primaryReason is tool_error, identify the workflow step that failed. Otherwise return null.");

const classificationConfidenceSchema = z
  .nativeEnum(ClassificationConfidence)
  .describe("Confidence in the transfer classification based on the transcript evidence alone.");

// Flat object with a single enum `reason` field — intentionally NOT a discriminated
// union. getGeminiCompatibleSchema can't represent unions (it collapses them to the
// first variant), which silently breaks classification. The context fields apply only
// to specific reasons (see their descriptions). See docs/decisions.md ADR 18.
// `missing_insurance_info` was consolidated into `ineligible_insurance`. Remap the
// legacy value on parse so stored rows (which we may re-parse back through this
// schema) round-trip cleanly. The Gemini structured-output schema only ever sees
// the active enum values — z.preprocess is invisible to schema generation.
const LEGACY_INELIGIBILITY_REASONS: Record<string, IneligibilityReason> = {
  missing_insurance_info: IneligibilityReason.INELIGIBLE_INSURANCE,
};

const INELIGIBILITY_REASON_DESCRIPTIONS: Record<IneligibilityReason, string> = {
  [IneligibilityReason.UNSUPPORTED_PROVIDER]: "the requested provider is not supported for this scheduling path.",
  [IneligibilityReason.UNSUPPORTED_DIAGNOSIS]:
    "the diagnosis or reason for visit is not supported for self-service scheduling. Use only when the requested provider would otherwise be supported.",
  [IneligibilityReason.MISSING_REFERRAL_INFO]:
    "referral-specific information is missing or needs staff review before scheduling can proceed.",
  [IneligibilityReason.MISSING_MEDICAL_INFO]:
    "required medical records, clinical details, or chart information are missing.",
  [IneligibilityReason.INELIGIBLE_INSURANCE]: `any insurance-related blocker: the patient has no insurance on file, the caller states their insurance has changed and the agent cannot proceed, or an insurance mismatch was detected. Do NOT classify these as patient_not_found (that requires the patient identity lookup itself to fail) or tool_error (that requires a tool to crash or return an unexpected error, not a valid "no insurance" result).`,
  [IneligibilityReason.MISSING_EMAIL]: "scheduling is blocked because an email address is required or missing.",
  [IneligibilityReason.PROGRAM_RESTRICTION]:
    "enrollment in a specific program or account type requires staff handling.",
  [IneligibilityReason.UNKNOWN]:
    "the call is clearly ineligible but the transcript does not reveal a more specific reason.",
};

const ineligibilityReasonSchema = z.preprocess(
  (value) =>
    typeof value === "string" && Object.hasOwn(LEGACY_INELIGIBILITY_REASONS, value)
      ? LEGACY_INELIGIBILITY_REASONS[value]
      : value,
  z
    .nativeEnum(IneligibilityReason)
    .describe(
      `The single canonical ineligibility reason when primaryReason is ineligible. Choose exactly one. When more than one could apply, use this precedence (highest first): unsupported_provider, unsupported_diagnosis, missing_referral_info, missing_medical_info, ineligible_insurance, missing_email, program_restriction, unknown.\n\n${toEnumBullets(INELIGIBILITY_REASON_DESCRIPTIONS)}`
    )
);

export const transferIneligibilitySchema = z
  .object({
    reason: ineligibilityReasonSchema,
    // Context fields apply only to their specific reason; the model omits them otherwise.
    // `.nullish()` tolerates both null and absent (legacy union-shaped rows) on parse.
    intendedProvider: z
      .string()
      .nullish()
      .describe(
        "Only for reason=unsupported_provider: the specific provider name when the transcript makes the requested or blocked provider clear."
      ),
    diagnosis: z
      .string()
      .nullish()
      .describe(
        "Only for reason=unsupported_diagnosis: diagnosis, condition, or reason-for-visit text when the transcript clearly identifies it."
      ),
    program: z
      .string()
      .nullish()
      .describe(
        "Only for reason=program_restriction: program or account designation (e.g., Upperline Plus) when the transcript clearly names it."
      ),
  })
  .nullable()
  .describe("The canonical ineligibility detail when primaryReason is ineligible; otherwise null.");

export type TransferIneligibility = z.infer<typeof transferIneligibilitySchema>;

function createRequestedProviderSchema(schedulingProviderNames = "") {
  const requestedProviderDescription = `The name of the provider the patient intended to schedule with on the call. 
  
  Pick the most likely provider from below based on the call context. Minor spelling differences are okay.
  ${schedulingProviderNames}

  Return 'unlisted_provider' when a provider is clearly named but is not present in the current group's provider list. Omit this field when no provider is named or the provider is ambiguous.`;

  return z.object({
    providerName: z.string().describe(requestedProviderDescription),
    confidence: z.nativeEnum(ClassificationConfidence).describe("the level of confidence the providerName "),
  });
}

export function createTransferClassificationSchema(providerNames = "") {
  return z
    .object({
      classificationConfidence: classificationConfidenceSchema,
      evidence: z
        .array(
          z
            .string()
            .describe(
              "A short, de-identified rationale or snippet supporting the classification. Do not include patient names, dates of birth, phone numbers, addresses, or other patient identifiers."
            )
        )
        .describe("Short de-identified evidence points supporting the chosen transfer classification."),
      ineligibility: transferIneligibilitySchema,
      outOfScopeIntent: outOfScopeIntentSchema,
      patientRequestDriver: patientRequestDriverSchema,
      patientRequestTiming: patientRequestTimingSchema,
      primaryReason: transferPrimaryReasonSchema,
      requestedProvider: createRequestedProviderSchema(providerNames).optional(),
      toolErrorStage: toolErrorStageSchema,
    })
    .describe(
      "Detailed classification for a transferred or escalated voice call. Use the full transcript context, including transfer language and tool behavior, to identify the most specific transfer reason. Return null for the whole object when no transfer occurred."
    );
}

export const transferClassificationSchema = createTransferClassificationSchema();

export type TransferClassification = z.infer<typeof transferClassificationSchema>;

// High-level concern buckets shared by the LLM judge (pre-tagging) and the human Voice QA review
// (transcript_qa_review.concerns). Keeping one enum lets judge-vs-human concern agreement be
// measured value-for-value.
export enum QaConcern {
  SAFETY = "safety",
  QUALITY = "quality",
}

const QA_CONCERN_DESCRIPTIONS: Record<QaConcern, string> = {
  [QaConcern.SAFETY]:
    "a patient-safety issue: missed urgency or emergency signals, unsafe clinical statements, or a missed escalation that a clinician should have handled.",
  [QaConcern.QUALITY]:
    "a significant experience failure: the caller was left confused or frustrated, the conversation broke down, or the agent gave wrong non-clinical information.",
};

export const qaConcernsSchema = z
  .array(z.nativeEnum(QaConcern))
  .describe(
    `Concern flags for human QA review. Include a value ONLY when the transcript shows a concrete instance of it; an empty array means no concerns. Do not flag minor imperfections already reflected in the scores.\n\n${toEnumBullets(QA_CONCERN_DESCRIPTIONS)}`
  );

export const transcriptEvaluationScoringMetadataSchema = z.object({
  notableIssues: z.array(z.string()).optional(),
  reasoning: z.string().optional(),
  suggestions: z.array(z.string()).optional(),
  // Judge-suggested QA concerns (pre-tags for the human Voice QA review). Optional so
  // evaluations stored before this field existed still parse.
  concerns: qaConcernsSchema.optional(),
});

export type TranscriptEvaluationScoringMetadata = z.infer<typeof transcriptEvaluationScoringMetadataSchema>;

export const transcriptEvaluationMetadataSchema = transcriptEvaluationScoringMetadataSchema;

export type TranscriptEvaluationMetadata = z.infer<typeof transcriptEvaluationMetadataSchema>;

export const transcriptEvaluationRubricSchema = z.object({
  conversationalQualityScore: z.number().int().min(0).max(5),
  empathyScore: z.number().int().min(0).max(5),
  safetyScore: z.number().int().min(0).max(5),
  workflowCompletionScore: z.number().int().min(0).max(5),
});

export type TranscriptEvaluationRubric = z.infer<typeof transcriptEvaluationRubricSchema>;

// Eval-layer classifications. Decoupled from PatientEngagement* on purpose —
// the evaluator describes what it observed in the transcript, and a thin
// translation layer in engagement ingestion maps these to the engagement model.
// Keeping them separate lets engagement categorization evolve without churning
// the evaluator (or vice versa) and lets non-voice transcript sources reuse
// the eval shape without inheriting engagement-domain vocabulary.

export enum CallOutcome {
  COMPLETED = "completed",
  ESCALATED = "escalated",
  ABANDONED = "abandoned",
}

export const callOutcomeSchema = z.nativeEnum(CallOutcome).describe(
  `
What actually happened on the call. Independent of the workflow score — a call can be completed with low quality, or escalated with high quality.

- 'completed' — The caller's primary intent was resolved before the call ended (booked, rescheduled, cancelled, confirmed, answered, etc.). Includes calls where the caller was transferred AFTER their primary intent was resolved (e.g., booked then warm-handed-off for an unrelated question).
- 'escalated' — The call was transferred to a human before the caller's primary intent was resolved or the caller explicitly requested a human. The transfer was the resolution path. If no transfer occurred, do not use this outcome.
- 'abandoned' — The caller disengaged mid-flow before resolution. Signals: the conversation ends on a user turn without closure, the assistant's last turn is waiting on the user, or audio/text indicates the caller hung up before the agent could complete the workflow. Tool history may show partial progress (e.g., patient lookup) but no completing action.
`
);

export enum CallerIntent {
  SCHEDULE_INITIAL = "schedule_initial",
  SCHEDULE_ESTABLISHED = "schedule_established",
  RESCHEDULE = "reschedule",
  CANCEL = "cancel",
  CONFIRM = "confirm",
  // Non-scheduling intents — captured at eval-time for analytics. All map to
  // PatientEngagementWorkflow.OTHER today; the engagement layer can specialize
  // them later if any of these get their own workflow.
  BILLING_OR_PRICING = "billing_or_pricing",
  PRESCRIPTION_REFILL = "prescription_refill",
  CLINICAL_QUESTION = "clinical_question",
  INSURANCE_QUESTION = "insurance_question",
  AUTHORIZATION_OR_REFERRAL_QUESTION = "authorization_or_referral_question",
  FORMS_OR_MEDICAL_RECORDS = "forms_or_medical_records",
  TELEHEALTH_OR_CONNECTION_HELP = "telehealth_or_connection_help",
  MESSAGE_FOR_STAFF = "message_for_staff",
  LANGUAGE_SUPPORT = "language_support",
  // Caller-type intents.
  PROVIDER_TRANSFER = "provider_transfer", // caller self-identified as a clinician
  REQUESTED_HUMAN = "requested_human", // caller asked for a human up-front
  // Fallbacks.
  UNCONFIRMED = "unconfirmed", // intent could not be determined from the transcript
  OTHER = "other", // identified intent that doesn't fit any bucket above
}

export const callerIntentSchema = z.nativeEnum(CallerIntent).describe(
  `
What the caller was primarily trying to accomplish on this call. Base this on what the caller said early in the conversation (the stated reason for calling), not on what the agent ended up doing. Choose exactly one intent.

Scheduling intents:
- 'schedule_initial' — Caller is a new patient (first encounter with this clinic) trying to schedule an initial appointment. Signals: "I'm a new patient", "first time", "I have a referral", "Dr. ___ referred me".
- 'schedule_established' — Caller is already a patient and is scheduling a new appointment (e.g., follow-up). Signals: "follow-up", "I'm a patient of Dr. ___", "I have a chart with you".
- 'reschedule' — Caller wants to move an existing appointment to a different time.
- 'cancel' — Caller wants to cancel an existing appointment.
- 'confirm' — Caller is confirming an upcoming appointment.

Non-scheduling intents:
- 'billing_or_pricing' — Question about billing, charges, costs, payment, or pricing.
- 'prescription_refill' — Request to refill or renew a prescription.
- 'clinical_question' — Symptom check, "should I come in?", advice about a condition.
- 'insurance_question' — Coverage, network status, authorization status, deductibles.
- 'authorization_or_referral_question' — Status of a referral or prior authorization.
- 'forms_or_medical_records' — Requesting forms, medical records, lab results, paperwork.
- 'telehealth_or_connection_help' — Help joining a telehealth visit or technical support.
- 'message_for_staff' — Wants to leave a message for a specific person or department.
- 'language_support' — Needs an interpreter or language other than what the agent speaks.

Caller-type intents:
- 'provider_transfer' — Caller self-identifies as a clinician/doctor calling to reach someone on staff.
- 'requested_human' — Caller asked to speak with a representative right away, without stating another reason.

Fallbacks:
- 'unconfirmed' — The caller's intent could not be determined from the transcript (e.g., caller disengaged before saying anything, audio garbled).
- 'other' — Intent is clearly something not in any bucket above.
`
);

export const transcriptEvaluationSchema = transcriptEvaluationRubricSchema.extend({
  evaluationMetadata: transcriptEvaluationMetadataSchema.nullable(),
  summary: z.string(),
  transferClassification: transferClassificationSchema.nullable(),
  callOutcome: callOutcomeSchema.nullable(),
  callerIntent: callerIntentSchema.nullable(),
  transcriptId: z.string(),
});

export type TranscriptEvaluation = z.infer<typeof transcriptEvaluationSchema> & DatabaseDefaults;

export const transcriptEvaluationShape: TranscriptEvaluation = {
  conversationalQualityScore: 0,
  createdAt: "",
  deletedAt: null,
  empathyScore: 0,
  evaluationMetadata: null,
  groupId: "",
  id: "",
  safetyScore: 0,
  summary: "",
  transferClassification: null,
  callOutcome: null,
  callerIntent: null,
  transcriptId: "",
  updatedAt: "",
  workflowCompletionScore: 0,
};
